import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { addReportToServer } from "../../functions/sendReport";
import { postMapGame } from "../../utils/mapGameApi";
import "./gameMap.css";

const S3 = (process.env.REACT_APP_S3_PATH || "").trim();
const BASE_W = 900;
const BASE_H = 600;
const MIN_POINTS_PER_CHARM = 5;
const MAX_POINTS_PER_CHARM = 100;
const MOBILE_DROP_TOLERANCE = 14;
const DESKTOP_DROP_TOLERANCE = 35;
const DEMO_CHARM_LIMIT = 3;

function appBaseUrl() {
  const b = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return b || window.location.origin;
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

function assetUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const rel = String(path).replace(/^\/+/, "");
  return `${S3}${encodeURIComponent(rel).replace(/%2F/g, "/")}`;
}

function pointsToSvg(points) {
  return (points || [])
    .map((p) => `${Number(p.x) * BASE_W},${Number(p.y) * BASE_H}`)
    .join(" ");
}

function toSlug(v) {
  return String(v || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function sameKey(a, b) {
  if (!a || !b) return false;
  return String(a) === String(b) || toSlug(a) === toSlug(b);
}

function parseTimeToSeconds(t) {
  if (!t || typeof t !== "string") return 0;
  const p = t.split(":").map((x) => parseInt(x, 10) || 0);
  if (p.length === 3) return p[0] * 3600 + p[1] * 60 + p[2];
  if (p.length === 2) return p[0] * 60 + p[1];
  return 0;
}

function formatMmSs(sec) {
  const t = Math.max(0, Math.floor(sec));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m < 10 ? `0${m}` : m}:${s < 10 ? `0${s}` : s}`;
}

function publicImgUrl(filename) {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${base}/img/${filename}`;
}

function getPointsPerCharm(themeData) {
  const raw = themeData?.points_per_charm ?? themeData?.points;
  const parsed = Number.parseInt(String(raw ?? ""), 10);
  if (!Number.isFinite(parsed)) return MIN_POINTS_PER_CHARM;
  return Math.max(MIN_POINTS_PER_CHARM, Math.min(MAX_POINTS_PER_CHARM, parsed));
}

export default function GameMap() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get("category") || "";
  const { data: themeData, status } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  // console.log(themeData);
  const category = useMemo(() => {
    const allCategories = Array.isArray(themeData?.categories) ? themeData.categories : [];
    if (!allCategories.length) return null;

    let found = allCategories.find((c) => c.id === categoryId);
    if (found) return found;

    found = allCategories.find((c) => toSlug(c.id) === toSlug(categoryId));
    if (found) return found;

    const list = Array.isArray(themeData?.categoriesList) ? themeData.categoriesList : [];
    const listEntry = list.find(
      (x) =>
        x.id === categoryId ||
        toSlug(x.id) === toSlug(categoryId) ||
        toSlug(x.name) === toSlug(categoryId)
    );
    if (listEntry) {
      const canonicalId = toSlug(listEntry.id);
      found = allCategories.find(
        (c) => c.id === listEntry.id || toSlug(c.id) === canonicalId || toSlug(c.name) === canonicalId
      );
      if (found) return found;
    }

    return null;
  }, [themeData, categoryId]);

  const mappings = useMemo(
    () => (Array.isArray(themeData?.countryMappings) ? themeData.countryMappings : []),
    [themeData]
  );
  const pointsPerCharm = useMemo(() => getPointsPerCharm(themeData), [themeData]);
  const mapFile = themeData?.mapImageRef?.file || "";
  const mapSrc = assetUrl(mapFile);
  const [zoom, setZoom] = useState(1);
  const [fitPercent, setFitPercent] = useState(100);
  const [placed, setPlaced] = useState({});
  const [draggingItem, setDraggingItem] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState("");
  const [rulesOpen, setRulesOpen] = useState(false);
  const [points, setPoints] = useState(0);
  const [timerSec, setTimerSec] = useState(0);
  const [hasStoredTimer, setHasStoredTimer] = useState(false);
  const [progressHydrated, setProgressHydrated] = useState(false);
  const [hintedItemId, setHintedItemId] = useState("");
  const [scorePopups, setScorePopups] = useState([]);
  const [mobileDragGhost, setMobileDragGhost] = useState({ visible: false, x: 0, y: 0, image: "" });
  /** HTML5 drag works on fine pointers; touch uses pointer/touch lift-and-drop instead. */
  const [allowHtml5Drag, setAllowHtml5Drag] = useState(
    () => !(typeof navigator !== "undefined" && navigator.maxTouchPoints > 0)
  );

  const categoryDoneRef = useRef(false);
  const mapWrapRef = useRef(null);
  const mapSvgRef = useRef(null);
  /** Touch / pen: item picked from tray (HTML5 drag does not run on most mobile browsers). */
  const touchSelectingRef = useRef(false);
  const touchTrayPickRef = useRef(null);
  /** Latest finger position while a tray pick is active (pointercancel / bad coords fallback). */
  const touchLastClientRef = useRef({ x: 0, y: 0, valid: false });
  const touchDragIdRef = useRef({ mode: "", id: null });
  const mapStateRef = useRef({ mappings, placed });
  const allowedByMappingRef = useRef(() => false);
  const placeItemOnMappingRef = useRef(() => false);
  const tryDropAtCoordsRef = useRef(() => false);
  const mapPanRef = useRef({ isPanning: false, startX: 0, startY: 0, startScrollLeft: 0, startScrollTop: 0 });
  const mapTouchPanRef = useRef({ isPanning: false, touchId: null, startX: 0, startY: 0, startScrollLeft: 0, startScrollTop: 0 });
  const reportIdRef = useRef(user?.reportId ?? null);
  const pointsRef = useRef(0);
  const demoLimitHandledRef = useRef(false);
  const dragItemRef = useRef(null);

  const allCategoryKeys = useMemo(() => {
    const list = Array.isArray(themeData?.categoriesList) ? themeData.categoriesList : [];
    const fromList = list.map((c) => toSlug(c?.id || c?.name)).filter(Boolean);
    if (fromList.length) return [...new Set(fromList)];
    const cats = Array.isArray(themeData?.categories) ? themeData.categories : [];
    const fromCats = cats.map((c) => toSlug(c?.id || c?.name)).filter(Boolean);
    return [...new Set(fromCats)];
  }, [themeData]);

  const allowedByMapping = (mapping, item) => {
    if (!category) return false;
    const a = Array.isArray(mapping.assignments) ? mapping.assignments : [];
    const row = a.find(
      (r) =>
        sameKey(r.categoryId, category.id) ||
        sameKey(r.categoryId, categoryId) ||
        sameKey(r.categoryId, category.name)
    );
    if (!row) return false;
    const ids = Array.isArray(row.itemIds) ? row.itemIds : [];
    if (ids.some((id) => sameKey(id, item.id) || sameKey(id, item.name))) return true;
    const mapId = item.mapId || item.countryId;
    return !!mapId && ids.some((id) => sameKey(id, mapId));
  };

  const availableItems = useMemo(() => {
    const used = new Set(Object.values(placed));
    return (Array.isArray(category?.items) ? category.items : []).filter((item) => !used.has(item.id));
  }, [category, placed]);

  const selectedItem = useMemo(
    () => availableItems.find((it) => it.id === selectedItemId) || null,
    [availableItems, selectedItemId]
  );
  const hintedItem = useMemo(
    () => availableItems.find((it) => it.id === hintedItemId) || null,
    [availableItems, hintedItemId]
  );
  const hintMappingIds = useMemo(() => {
    if (!hintedItem) return new Set();
    return new Set(
      mappings
        .filter((m) => !placed[m.mappingId] && allowedByMapping(m, hintedItem))
        .map((m) => m.mappingId)
    );
  }, [hintedItem, mappings, placed]);

  const getMappingCentroid = (mapping) => {
    const pts = Array.isArray(mapping?.area?.points) ? mapping.area.points : [];
    if (!pts.length) return { x: 0, y: 0 };
    const sum = pts.reduce((acc, p) => ({ x: acc.x + Number(p.x), y: acc.y + Number(p.y) }), { x: 0, y: 0 });
    return { x: sum.x / pts.length, y: sum.y / pts.length };
  };

  const getMappingAnchorSvg = (mapping) => {
    const pts = Array.isArray(mapping?.area?.points) ? mapping.area.points : [];
    const p0 = pts[0];
    if (p0) {
      return { x: Number(p0.x) * BASE_W, y: Number(p0.y) * BASE_H };
    }
    const c = getMappingCentroid(mapping);
    return { x: c.x * BASE_W, y: c.y * BASE_H };
  };

  const getMappingPointsSvg = (mapping) => {
    const pts = Array.isArray(mapping?.area?.points) ? mapping.area.points : [];
    return pts
      .map((p) => ({ x: Number(p.x) * BASE_W, y: Number(p.y) * BASE_H }))
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
  };

  const pointInPolygonSvg = (point, poly) => {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x;
      const yi = poly[i].y;
      const xj = poly[j].x;
      const yj = poly[j].y;
      const crosses = (yi > point.y) !== (yj > point.y);
      if (!crosses) continue;
      const denom = yj - yi || 1e-9;
      const xAtY = ((xj - xi) * (point.y - yi)) / denom + xi;
      if (point.x < xAtY) inside = !inside;
    }
    return inside;
  };

  const distanceToSegmentSvg = (p, a, b) => {
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const wx = p.x - a.x;
    const wy = p.y - a.y;
    const vv = vx * vx + vy * vy;
    if (!vv) return Math.hypot(p.x - a.x, p.y - a.y);
    const t = Math.max(0, Math.min(1, (wx * vx + wy * vy) / vv));
    const projX = a.x + t * vx;
    const projY = a.y + t * vy;
    return Math.hypot(p.x - projX, p.y - projY);
  };

  const distanceToMappingSvg = (mapping, x, y) => {
    const poly = getMappingPointsSvg(mapping);
    if (poly.length < 3) {
      const c = getMappingCentroid(mapping);
      return Math.hypot(c.x * BASE_W - x, c.y * BASE_H - y);
    }
    const point = { x, y };
    if (pointInPolygonSvg(point, poly)) return 0;
    let min = Number.POSITIVE_INFINITY;
    for (let i = 0; i < poly.length; i += 1) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      min = Math.min(min, distanceToSegmentSvg(point, a, b));
    }
    return min;
  };

  const placeItemOnMapping = (mapping, item) => {
    if (!mapping || !item) return false;
    if (placed[mapping.mappingId]) return false;
    if (Object.values(placed).includes(item.id)) return false;
    if (!allowedByMapping(mapping, item)) return false;
    const nextPlaced = { ...placed, [mapping.mappingId]: item.id };
    const nextPoints = pointsRef.current + pointsPerCharm;
    const anchor = getMappingAnchorSvg(mapping);
    const popupId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    setScorePopups((prev) => [...prev, { id: popupId, x: anchor.x, y: anchor.y, amount: pointsPerCharm }]);
    setPlaced(nextPlaced);
    setPoints((p) => p + pointsPerCharm);
    setDraggingItem(null);
    dragItemRef.current = null;
    setSelectedItemId("");
    if (hintedItemId === item.id) setHintedItemId("");

    const isDemoUser = user?.source === "DEMO" || user?.role === "DEMO" || user?.role === "demobypass";
    if (isDemoUser && !demoLimitHandledRef.current && Object.keys(nextPlaced).length >= DEMO_CHARM_LIMIT) {
      demoLimitHandledRef.current = true;
      sessionStorage.setItem("spotThankYouMsg", "Thanks for playing (demo).");
      setTimeout(() => {
        Swal.fire({
          icon: "success",
          title: "Thank you for playing.",
          text: "Subscribe to any PLAN to play with your peers.",
        }).then(() => {
          window.location.href = demoPlansUrl();
        });
      }, 0);
    }

    if (user) {
      const answerLabels = Object.values(nextPlaced)
        .map((id) => (Array.isArray(category?.items) ? category.items : []).find((it) => it.id === id)?.name || String(id))
        .filter(Boolean);
      addReportToServer(user, {
        points: nextPoints,
        elapsedSeconds: timerSec,
        answers: answerLabels,
        reportIdRef,
      });
    }
    return true;
  };

  useEffect(() => {
    mapStateRef.current = { mappings, placed };
    allowedByMappingRef.current = allowedByMapping;
    placeItemOnMappingRef.current = placeItemOnMapping;
  });

  tryDropAtCoordsRef.current = (clientX, clientY, item) => {
    const svg = mapSvgRef.current;
    if (!svg || !item) return false;
    const { mappings: mp, placed: pl } = mapStateRef.current;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;
    const sx = ((clientX - rect.left) / rect.width) * BASE_W;
    const sy = ((clientY - rect.top) / rect.height) * BASE_H;
    const allowed = allowedByMappingRef.current;
    const candidates = mp.filter((m) => !pl[m.mappingId] && allowed(m, item));
    if (!candidates.length) return false;
    const touch =
      typeof navigator !== "undefined" && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 0;
    const maxDist = touch ? MOBILE_DROP_TOLERANCE : DESKTOP_DROP_TOLERANCE;
    const nearest = candidates
      .map((m) => ({
        mapping: m,
        dist: distanceToMappingSvg(m, sx, sy),
      }))
      .sort((a, b) => a.dist - b.dist)[0];
    if (nearest && nearest.dist <= maxDist) {
      return placeItemOnMappingRef.current(nearest.mapping, item);
    }
    return false;
  };

  useEffect(() => {
    const finishTouchTray = (clientX, clientY, opts = {}) => {
      const preferLastKnown = opts.preferLastKnown === true;
      const pick = touchTrayPickRef.current;
      if (!pick) return;
      let x = clientX;
      let y = clientY;
      const last = touchLastClientRef.current;
      if (preferLastKnown && last.valid) {
        x = last.x;
        y = last.y;
      } else if (last.valid) {
        const bad =
          !Number.isFinite(x) ||
          !Number.isFinite(y) ||
          (Math.abs(x) < 1 && Math.abs(y) < 1);
        if (bad) {
          x = last.x;
          y = last.y;
        }
      }
      touchTrayPickRef.current = null;
      touchDragIdRef.current = { mode: "", id: null };
      touchLastClientRef.current = { x: 0, y: 0, valid: false };
      setDraggingItem(null);
      dragItemRef.current = null;
      setMobileDragGhost({ visible: false, x: 0, y: 0, image: "" });
      tryDropAtCoordsRef.current(x, y, pick);
    };

    const onPointerEnd = (e) => {
      const hasTouch = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
      if (e.pointerType === "mouse" && !hasTouch) return;
      if (touchDragIdRef.current.mode === "pointer" && touchDragIdRef.current.id != null && e.pointerId !== touchDragIdRef.current.id) return;
      finishTouchTray(e.clientX, e.clientY, { preferLastKnown: false });
    };

    const onPointerCancel = (e) => {
      const hasTouch = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
      if (e.pointerType === "mouse" && !hasTouch) return;
      if (touchDragIdRef.current.mode === "pointer" && touchDragIdRef.current.id != null && e.pointerId !== touchDragIdRef.current.id) return;
      finishTouchTray(e.clientX, e.clientY, { preferLastKnown: true });
    };

    const onTouchEnd = (e) => {
      const changed = Array.from(e.changedTouches || []);
      let t = changed[0];
      if (touchDragIdRef.current.mode === "touch" && touchDragIdRef.current.id != null) {
        t = changed.find((x) => x.identifier === touchDragIdRef.current.id) || t;
      }
      if (!t) return;
      finishTouchTray(t.clientX, t.clientY, { preferLastKnown: false });
    };

    const onPointerMoveTrack = (e) => {
      if (!touchTrayPickRef.current) return;
      const hasTouch = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
      if (e.pointerType === "mouse" && !hasTouch) return;
      if (touchDragIdRef.current.mode === "pointer" && touchDragIdRef.current.id != null && e.pointerId !== touchDragIdRef.current.id) return;
      touchLastClientRef.current = { x: e.clientX, y: e.clientY, valid: true };
      const pick = touchTrayPickRef.current;
      if (!pick) return;
      setMobileDragGhost((prev) => ({
        visible: true,
        x: e.clientX,
        y: e.clientY,
        image: prev.image || assetUrl(pick.image),
      }));
    };

    const onTouchMoveTrack = (e) => {
      if (!touchTrayPickRef.current) return;
      const touches = Array.from(e.touches || []);
      let t = touches[0];
      if (touchDragIdRef.current.mode === "touch" && touchDragIdRef.current.id != null) {
        t = touches.find((x) => x.identifier === touchDragIdRef.current.id) || t;
      }
      if (!t) return;
      touchLastClientRef.current = { x: t.clientX, y: t.clientY, valid: true };
      const pick = touchTrayPickRef.current;
      if (!pick) return;
      setMobileDragGhost((prev) => ({
        visible: true,
        x: t.clientX,
        y: t.clientY,
        image: prev.image || assetUrl(pick.image),
      }));
    };

    window.addEventListener("pointermove", onPointerMoveTrack, true);
    window.addEventListener("pointerup", onPointerEnd, true);
    window.addEventListener("pointercancel", onPointerCancel, true);
    window.addEventListener("touchmove", onTouchMoveTrack, { capture: true, passive: true });
    window.addEventListener("touchend", onTouchEnd, { capture: true, passive: true });
    return () => {
      window.removeEventListener("pointermove", onPointerMoveTrack, true);
      window.removeEventListener("pointerup", onPointerEnd, true);
      window.removeEventListener("pointercancel", onPointerCancel, true);
      window.removeEventListener("touchmove", onTouchMoveTrack, { capture: true });
      window.removeEventListener("touchend", onTouchEnd, { capture: true });
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mqCoarse = window.matchMedia("(pointer: coarse)");
    const mqHoverNone = window.matchMedia("(hover: none)");
    const sync = () => {
      const coarse = mqCoarse.matches;
      const hoverNone = mqHoverNone.matches;
      const touchPoints = typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0;
      if (touchPoints > 0) {
        setAllowHtml5Drag(false);
        return;
      }
      const touchPrimary = coarse || (hoverNone && touchPoints > 0);
      setAllowHtml5Drag(!touchPrimary);
    };
    sync();
    const mqs = [mqCoarse, mqHoverNone];
    mqs.forEach((mq) => {
      if (mq.addEventListener) mq.addEventListener("change", sync);
      else mq.addListener(sync);
    });
    return () => {
      mqs.forEach((mq) => {
        if (mq.removeEventListener) mq.removeEventListener("change", sync);
        else mq.removeListener(sync);
      });
    };
  }, []);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  useEffect(() => {
    if (user?.reportId) {
      reportIdRef.current = user.reportId;
      return;
    }
    if (user?.userId && user?.sessionId) {
      try {
        const s = sessionStorage.getItem(`tot_report_${user.sessionId}_${user.userId}`);
        if (s) reportIdRef.current = s;
      } catch {
        /* ignore */
      }
    }
  }, [user?.reportId, user?.userId, user?.sessionId]);

  useEffect(() => {
    const nextBack = user?.backButtonRedirect || "/login?&save=true";
    dispatch(setBackButtonUrl(nextBack));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    demoLimitHandledRef.current = false;
  }, [category?.id]);

  useEffect(() => {
    categoryDoneRef.current = false;
  }, [category?.id]);

  useEffect(() => {
    setScorePopups([]);
  }, [category?.id]);

  useEffect(() => {
    const stopPan = () => {
      mapPanRef.current.isPanning = false;
    };
    window.addEventListener("mouseup", stopPan);
    return () => window.removeEventListener("mouseup", stopPan);
  }, []);

  useEffect(() => {
    const recomputeFit = () => {
      const wrap = mapWrapRef.current;
      if (!wrap) return;
      const cw = wrap.clientWidth || 0;
      const ch = wrap.clientHeight || 0;
      if (!cw || !ch) return;
      const fitByHeightWidth = (ch * BASE_W) / BASE_H;
      const pct = Math.min(100, (fitByHeightWidth / cw) * 100);
      setFitPercent(Math.max(60, Math.min(100, pct)));
    };

    recomputeFit();
    const raf = window.requestAnimationFrame(recomputeFit);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(recomputeFit) : null;
    if (ro && mapWrapRef.current) ro.observe(mapWrapRef.current);
    window.addEventListener("resize", recomputeFit);
    return () => {
      window.cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", recomputeFit);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!category?.id) {
      setProgressHydrated(false);
      return undefined;
    }
    if (!user || !adminToken) {
      setProgressHydrated(true);
      setHasStoredTimer(false);
      return undefined;
    }
    setProgressHydrated(false);
    (async () => {
      try {
        const uid = user.userId || user.id;
        const data = await postMapGame(adminToken, "/mapGameProgress", {
          userId: uid,
          email: user.email,
          name: user.name,
        });
        if (cancelled) return;
        const ckey = toSlug(category.id);
        const nextPlaced = data.placedByCategory?.[ckey];
        if (nextPlaced && typeof nextPlaced === "object") setPlaced(nextPlaced);
        else setPlaced({});
        if (typeof data.points === "number") setPoints(data.points);
        const ts = typeof data.timerSec === "number" ? data.timerSec : 0;
        setTimerSec(ts);
        setHasStoredTimer(ts > 0);
      } catch (e) {
        console.error("mapGameProgress:", e);
        if (!cancelled) {
          setPlaced({});
        }
      } finally {
        if (!cancelled) setProgressHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [category?.id, user, adminToken]);

  const skipPeriodicSave = availableItems.length === 0;

  useEffect(() => {
    if (!progressHydrated || !category?.id || !user || !adminToken) return undefined;
    if (skipPeriodicSave) return undefined;
    const t = window.setTimeout(async () => {
      try {
        const uid = user.userId || user.id;
        const categoryPoints = Object.keys(placed).length * pointsPerCharm;
        await postMapGame(adminToken, "/mapGameSave", {
          userId: uid,
          email: user.email,
          name: user.name,
          categoryId: toSlug(category.id),
          placed,
          points,
          timerSec,
          completed: false,
          categoryPoints,
        });
      } catch (e) {
        console.error("mapGameSave:", e);
      }
    }, 800);
    return () => window.clearTimeout(t);
  }, [placed, points, timerSec, progressHydrated, category, user, adminToken, skipPeriodicSave, pointsPerCharm]);

  useEffect(() => {
    if (!progressHydrated) return undefined;
    if (!themeData) return undefined;
    const useTimeout = themeData?.use_timeout === true || themeData?.use_timeout === "true";
    const baseSec = parseTimeToSeconds(themeData?.timer || "00:00");
    if (!hasStoredTimer) {
      setTimerSec(useTimeout ? baseSec : 0);
    }
    const id = window.setInterval(() => {
      setTimerSec((prev) => {
        if (useTimeout) return Math.max(0, prev - 1);
        if (prev >= 3600) return 0;
        return prev + 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [themeData, hasStoredTimer, progressHydrated]);

  useEffect(() => {
    if (!category) return undefined;
    const catItems = Array.isArray(category.items) ? category.items : [];
    if (!catItems.length) return undefined;
    if (availableItems.length > 0) return undefined;
    if (!user || !adminToken) return undefined;
    if (categoryDoneRef.current) return undefined;
    categoryDoneRef.current = true;

    (async () => {
      try {
        const uid = user.userId || user.id;
        const categoryPoints = Object.keys(placed).length * pointsPerCharm;
        const data = await postMapGame(adminToken, "/mapGameSave", {
          userId: uid,
          email: user.email,
          name: user.name,
          categoryId: toSlug(category.id),
          placed,
          points,
          timerSec,
          completed: true,
          categoryPoints,
        });
        const done = new Set(
          (Array.isArray(data.completedCategories) ? data.completedCategories : [])
            .map((v) => toSlug(v))
            .filter(Boolean)
        );
        const doneCount = allCategoryKeys.filter((k) => done.has(k)).length;
        const backendDoneCount = Math.max(0, parseInt(String(data.completedCount ?? 0), 10) || 0);
        const isAllDoneByCount = allCategoryKeys.length > 0 && backendDoneCount >= allCategoryKeys.length;
        const isAllDoneByKeys = allCategoryKeys.length > 0 && doneCount >= allCategoryKeys.length;
        if (isAllDoneByCount || isAllDoneByKeys) {
          navigate("/leaderboard", { replace: true });
          return;
        }
        navigate(`/category-leaderboard?category=${encodeURIComponent(toSlug(category.id))}`, { replace: true });
      } catch (e) {
        console.error("mapGameSave complete:", e);
        categoryDoneRef.current = false;
      }
    })();
    return undefined;
  }, [availableItems.length, category, allCategoryKeys, navigate, placed, points, timerSec, user, adminToken, pointsPerCharm]);

  const armCharmPickFromTrayEvent = (item, ev) => {
    if (!ev?.target?.closest || ev.target.closest(".gm-item-hint-btn")) return;
    touchTrayPickRef.current = item;
    setDraggingItem(item);
    dragItemRef.current = item;
    setSelectedItemId(item.id);
    if (selectedItemId !== item.id) {
      touchSelectingRef.current = true;
    }
    if (ev.type === "touchstart" && ev.touches?.[0]) {
      const t0 = ev.touches[0];
      touchDragIdRef.current = { mode: "touch", id: t0.identifier };
      touchLastClientRef.current = { x: t0.clientX, y: t0.clientY, valid: true };
      setMobileDragGhost({
        visible: true,
        x: t0.clientX,
        y: t0.clientY,
        image: assetUrl(item.image),
      });
    } else if (Number.isFinite(ev.clientX) && Number.isFinite(ev.clientY)) {
      const pointerId = Number.isFinite(ev.pointerId) ? ev.pointerId : null;
      touchDragIdRef.current = { mode: "pointer", id: pointerId };
      touchLastClientRef.current = { x: ev.clientX, y: ev.clientY, valid: true };
      setMobileDragGhost({
        visible: true,
        x: ev.clientX,
        y: ev.clientY,
        image: assetUrl(item.image),
      });
    }
  };

  if (status !== "succeeded" || !themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (!category) {
    return (
      <div className="gm-page">
        <h2 className="gm-title">Category not found</h2>
        <button className="gm-next" onClick={() => navigate("/categories")}>
          Back To Categories
        </button>
      </div>
    );
  }

  if (!progressHydrated) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div className="gm-page">
      <div className="gm-hud gm-hud-left">
        <div className="gm-hud-pill">
          <img src={publicImgUrl("timer-img.png")} alt="" />
          <span className="gm-hud-timer">{formatMmSs(timerSec)}</span>
        </div>
        <button className="gm-hud-pill gm-hud-btn" onClick={() => setRulesOpen(true)}>
          <img src={publicImgUrl("rules-img.png")} alt="Rules" />
        </button>
      </div>
      <div className="gm-hud gm-hud-right">
        <div className="gm-hud-pill">
          <img src={publicImgUrl("point-img.png")} alt="" />
          <span>{points}</span>
        </div>
      </div>

      <h1 className="gm-title">{category.name.replace(/\s*Mode\s*$/i, "").toUpperCase()}</h1>
      <div className="gm-layout">
        <aside className="gm-items">
          {availableItems.map((item) => (
            <div
              key={item.id}
              draggable={allowHtml5Drag}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = "move";
                e.dataTransfer.setData("text/plain", item.id);
                setDraggingItem(item);
                dragItemRef.current = item;
                setSelectedItemId(item.id);
              }}
              onDragEnd={() => {
                setDraggingItem(null);
                dragItemRef.current = null;
              }}
              onTouchStart={(e) => armCharmPickFromTrayEvent(item, e)}
              onPointerDown={(e) => {
                if (e.pointerType === "mouse") return;
                armCharmPickFromTrayEvent(item, e);
              }}
              onClick={() => {
                if (touchSelectingRef.current) {
                  touchSelectingRef.current = false;
                  return;
                }
                setSelectedItemId((prev) => (prev === item.id ? "" : item.id));
              }}
              className={`gm-item-card${selectedItemId === item.id ? " is-selected" : ""}`}
            >
              <img src={assetUrl(item.image)} alt={item.name} className="gm-item-img" />
              <button
                type="button"
                className="gm-item-hint-btn"
                title="Show drop area (+10 sec)"
                onClick={(e) => {
                  e.stopPropagation();
                  setHintedItemId(item.id);
                  setSelectedItemId(item.id);
                  setTimerSec((t) => t + 10);
                }}
              >
                i
              </button>
            </div>
          ))}
        </aside>
        <section className="gm-map-panel">
          <div
            ref={mapWrapRef}
            className={`gm-map-wrap${zoom > 1 ? " is-zoomed" : ""}`}
            onMouseDown={(e) => {
              if (zoom <= 1) return;
              if (e.button !== 0) return;
              const wrap = mapWrapRef.current;
              if (!wrap) return;
              mapPanRef.current.isPanning = true;
              mapPanRef.current.startX = e.clientX;
              mapPanRef.current.startY = e.clientY;
              mapPanRef.current.startScrollLeft = wrap.scrollLeft;
              mapPanRef.current.startScrollTop = wrap.scrollTop;
            }}
            onMouseMove={(e) => {
              if (!mapPanRef.current.isPanning) return;
              const wrap = mapWrapRef.current;
              if (!wrap) return;
              const dx = e.clientX - mapPanRef.current.startX;
              const dy = e.clientY - mapPanRef.current.startY;
              wrap.scrollLeft = mapPanRef.current.startScrollLeft - dx;
              wrap.scrollTop = mapPanRef.current.startScrollTop - dy;
            }}
            onMouseUp={() => {
              mapPanRef.current.isPanning = false;
            }}
            onMouseLeave={() => {
              mapPanRef.current.isPanning = false;
            }}
            onTouchStart={(e) => {
              if (zoom <= 1) return;
              if (touchTrayPickRef.current) return;
              const wrap = mapWrapRef.current;
              const t = e.touches?.[0];
              if (!wrap || !t) return;
              mapTouchPanRef.current.isPanning = true;
              mapTouchPanRef.current.touchId = t.identifier;
              mapTouchPanRef.current.startX = t.clientX;
              mapTouchPanRef.current.startY = t.clientY;
              mapTouchPanRef.current.startScrollLeft = wrap.scrollLeft;
              mapTouchPanRef.current.startScrollTop = wrap.scrollTop;
            }}
            onTouchMove={(e) => {
              const pan = mapTouchPanRef.current;
              if (!pan.isPanning) return;
              const wrap = mapWrapRef.current;
              if (!wrap) return;
              const touches = Array.from(e.touches || []);
              const t = touches.find((x) => x.identifier === pan.touchId) || touches[0];
              if (!t) return;
              e.preventDefault();
              const dx = t.clientX - pan.startX;
              const dy = t.clientY - pan.startY;
              wrap.scrollLeft = pan.startScrollLeft - dx;
              wrap.scrollTop = pan.startScrollTop - dy;
            }}
            onTouchEnd={(e) => {
              const pan = mapTouchPanRef.current;
              if (!pan.isPanning) return;
              const ended = Array.from(e.changedTouches || []).some((x) => x.identifier === pan.touchId);
              if (!ended && e.touches?.length) return;
              mapTouchPanRef.current.isPanning = false;
              mapTouchPanRef.current.touchId = null;
            }}
            onTouchCancel={() => {
              mapTouchPanRef.current.isPanning = false;
              mapTouchPanRef.current.touchId = null;
            }}
          >
            <div className="gm-map-scale" style={{ width: `${fitPercent * zoom}%` }}>
            <svg
              ref={mapSvgRef}
              className="gm-map-svg"
              viewBox={`0 0 ${BASE_W} ${BASE_H}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const item = dragItemRef.current || draggingItem || selectedItem;
                if (!item) return;
                tryDropAtCoordsRef.current(e.clientX, e.clientY, item);
              }}
            >
              {mapSrc ? <image href={mapSrc} x="0" y="0" width={BASE_W} height={BASE_H} /> : null}
              {mappings.map((m) => (
                <polygon
                  key={m.mappingId}
                  points={pointsToSvg(m.area?.points)}
                  className={`gm-map-zone${hintMappingIds.has(m.mappingId) ? " is-hint" : ""}`}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => {
                    const item = selectedItem || draggingItem;
                    if (!item) return;
                    placeItemOnMapping(m, item);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    placeItemOnMapping(m, dragItemRef.current || draggingItem || selectedItem);
                  }}
                />
              ))}
              {mappings.map((m) => {
                const itemId = placed[m.mappingId];
                if (!itemId) return null;
                const item = category.items.find((it) => it.id === itemId);
                if (!item) return null;
                const p = m.area?.points?.[0];
                if (!p) return null;
                return (
                  <image
                    key={`${m.mappingId}-img`}
                    href={assetUrl(item.image)}
                    x={Number(p.x) * BASE_W - 22}
                    y={Number(p.y) * BASE_H - 22}
                    width="44"
                    height="44"
                  />
                );
              })}
              {scorePopups.map(({ id, x, y, amount }) => (
                <g key={id} transform={`translate(${x}, ${y})`}>
                  <g
                    className="gm-score-popup"
                    onAnimationEnd={(e) => {
                      if (e.animationName !== "gm-score-float") return;
                      setScorePopups((prev) => prev.filter((s) => s.id !== id));
                    }}
                  >
                    <text x={0} y={-30} textAnchor="middle" className="gm-score-popup-text">
                      +{amount}
                    </text>
                  </g>
                </g>
              ))}
            </svg>
          </div>
          </div>
          <div className="gm-zoom">
            <button type="button" onClick={() => setZoom((z) => Math.max(0.7, z - 0.1))}>
              -
            </button>
            <button type="button" onClick={() => setZoom((z) => Math.min(1.8, z + 0.1))}>
              +
            </button>
          </div>
        </section>
      </div>
      {/* <button type="button" className="gm-next" onClick={() => navigate("/categories")}>
        Categories
      </button> */}

      {rulesOpen && (
        <div className="gm-rules-overlay" onClick={() => setRulesOpen(false)}>
          <div className="gm-rules-dialog" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="gm-rules-close"
              aria-label="Close rules popup"
              onClick={() => setRulesOpen(false)}
            >
              &#10005;
            </button>
            <h3 style={{ color: themeData?.text_color }}>How to Play</h3>
            <ul>
              {(Array.isArray(themeData?.rules) ? themeData.rules : []).map((r, i) => (
                <li key={`${r}-${i}`} style={{ color: themeData?.text_color }}>{r}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
      {mobileDragGhost.visible && mobileDragGhost.image ? (
        <div
          className="gm-drag-ghost"
          style={{
            left: `${mobileDragGhost.x}px`,
            top: `${mobileDragGhost.y}px`,
            backgroundImage: `url(${mobileDragGhost.image})`,
          }}
          aria-hidden
        />
      ) : null}
    </div>
  );
}
